<template>
  <div class="journal-card" @click="$emit('click')">
    <div class="card-main">
      <div class="card-cover">
        <img v-if="journal.cover_image || journal.cover" :src="journal.cover_image || journal.cover" :alt="journal.name" />
        <div v-else class="cover-placeholder">封面</div>
        <span v-if="journal.current_issue && isPaused" class="badge badge-pause">暂停收稿</span>
      </div>
      <div class="card-body">
        <div class="card-title-row">
          <h3 class="card-title">{{ journal.name }}</h3>
          <span v-if="!journal.price_visible" class="card-price-tip">登录后可见</span>
          <span v-else class="card-code">{{ priceCode }}</span>
        </div>
        <div class="card-meta">{{ metaLine }}</div>
        <div class="card-chars" v-if="journal.page_chars">{{ journal.page_chars }}</div>
        <div class="card-status" v-if="journal.current_issue">{{ journal.current_issue }}</div>
        <div class="card-desc" v-if="journal.selling_points">{{ journal.selling_points }}</div>
        <div class="card-stars" v-if="reviewStars">
          <span v-for="i in 5" :key="i" class="star" :class="{ on: i <= reviewStars }">★</span>
        </div>
        <div class="card-tags" v-if="tagsList.length">
          <span v-for="(t, idx) in tagsList" :key="(t || '') + '-' + idx" class="tag">{{ t }}</span>
        </div>
        <div class="card-actions">
          <a href="javascript:;" @click.prevent="$emit('click')">封面查看</a>
          <a href="javascript:;" @click.prevent="$emit('click')">基本信息</a>
          <a href="javascript:;" @click.prevent>总署链接</a>
          <a href="javascript:;" @click.prevent>收录网址</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
export default {
  name: 'JournalCard',
  props: {
    journal: { type: Object, required: true }
  },
  emits: ['click'],
  setup(props) {
    const directionName = computed(() =>
      (props.journal.direction && props.journal.direction.name) ? props.journal.direction.name : ''
    )
    const metaLine = computed(() => {
      const parts = [
        props.journal.level,
        props.journal.index_db,
        props.journal.publish_cycle,
        directionName.value
      ].filter(Boolean)
      return parts.join(' / ')
    })
    const priceCode = computed(() => {
      if (!props.journal.price_visible) return ''
      const min = props.journal.price_min ?? props.journal.price
      const label = props.journal.price_label
      let text = ''
      if (min != null && min > 0) {
        if (min >= 10000) text = (min / 10000).toFixed(0) + 'XXX'
        else if (min >= 1000) text = (min / 1000).toFixed(0) + 'XXX'
        else if (min >= 100) text = (min / 100).toFixed(0) + 'XX'
        else text = min + 'XX'
      }
      return label ? `${label} ${text}`.trim() : text
    })
    const reviewStars = computed(() => {
      const d = props.journal.review_difficulty
      if (!d) return 0
      const m = String(d).match(/(\d)/)
      return m ? Math.min(5, Math.max(0, parseInt(m[1], 10))) : 0
    })
    const tagsList = computed(() => {
      const j = props.journal
      const arr = []
      if (j.tags_arr && j.tags_arr.length) arr.push(...j.tags_arr)
      else if (j.tags) arr.push(...String(j.tags).split(',').map(s => s.trim()).filter(Boolean))
      return arr.slice(0, 4)
    })
    const isPaused = computed(() => {
      const s = (props.journal.current_issue || '').toLowerCase()
      return /暂停|停收/.test(s)
    })
    return { metaLine, priceCode, reviewStars, tagsList, isPaused }
  }
}
</script>

<style scoped>
.journal-card {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,.08);
  cursor: pointer;
  transition: box-shadow 0.2s;
}
.journal-card:hover { box-shadow: 0 2px 12px rgba(0,0,0,.12); }
.card-main { display: flex; }
.card-cover {
  width: 100px;
  min-width: 100px;
  height: 130px;
  position: relative;
  background: #f0f0f0;
}
.card-cover img { width: 100%; height: 100%; object-fit: cover; }
.cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #999;
}
.badge {
  position: absolute;
  top: 0;
  left: 0;
  padding: 2px 6px;
  font-size: 11px;
  color: #fff;
  background: rgba(200,0,0,.85);
}
.card-body { flex: 1; padding: 12px 14px; display: flex; flex-direction: column; gap: 6px; }
.card-title-row { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
.card-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--color-title-accent);
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.card-code { font-size: 13px; color: var(--color-primary); flex-shrink: 0; }
.card-price-tip { font-size: 12px; color: var(--color-text-secondary); flex-shrink: 0; }
.card-meta { font-size: 12px; color: var(--color-text-secondary); }
.card-chars, .card-status, .card-desc { font-size: 12px; color: #666; }
.card-stars { font-size: 14px; }
.star { color: #ddd; }
.star.on { color: #f7ba2a; }
.card-tags { display: flex; flex-wrap: wrap; gap: 4px; }
.tag {
  padding: 2px 6px;
  font-size: 11px;
  background: var(--color-tag-bg);
  color: var(--color-primary);
  border-radius: 2px;
}
.card-actions {
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid #eee;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.card-actions a { font-size: 12px; color: var(--color-text-secondary); }
.card-actions a:hover { color: var(--color-primary); }
@media (max-width: 768px) {
  .journal-card { border-radius: 6px; }
  .card-cover { width: 88px; min-width: 88px; height: 116px; }
  .card-body { padding: 10px 12px; gap: 4px; }
  .card-title { font-size: 14px; }
  .card-actions {
    padding-top: 10px;
    gap: 10px;
  }
  .card-actions a {
    min-height: 32px;
    display: inline-flex;
    align-items: center;
  }
}
</style>
